// Centralized color palette for AviaBozor.uz
// Colors extracted from brand logo and optimized for UI consistency
const palette = {
  // ========================================
  // BASE COLORS
  // ========================================

  // White shades
  white: "#ffffff",
  whiteAlt: "#fff",

  // Black shades
  black: "#000000",
  blackAlt: "#000",

  // Gray scale
  gray: {
    50: "#fafafa",
    100: "#f5f5f5",
    150: "#f1f5f9",
    200: "#eeeeee",
    250: "#e5e5e5",
    300: "#dfdfdf",
    350: "#d0d0d0",
    400: "#cccccc",
    425: "#b8b8b8",
    450: "#c4c4c4",
    500: "#b7b7b7",
    550: "#b0b0b0",
    600: "#9e9e9e",
    650: "#999999",
    675: "#808080",
    700: "#666666",
    750: "#64748b",
    800: "#333333",
    850: "#334155",
    900: "#1a1a2e",
    950: "#151515",
  },

  // ========================================
  // BRAND COLORS (AviaBozor)
  // ========================================

  // Primary colors (Orange - from dome/building)
  primary: {
    main: "#F7941D", // Main orange
    dark: "#E8850A", // Darker orange for hover
    darker: "#D47600", // Even darker for active states
    light: "#FFA94D", // Lighter orange
    lighter: "#FFBE73", // Very light orange
    bg: "rgba(247, 148, 29, 0.1)",
    bgLight: "rgba(247, 148, 29, 0.05)",
    shadow: "rgba(247, 148, 29, 0.35)",
    shadowHover: "rgba(247, 148, 29, 0.45)",
    shadowLight: "rgba(247, 148, 29, 0.3)",
    shadowLighter: "rgba(247, 148, 29, 0.2)",
  },

  // primary: {
  //   main: "#8b5cf6",
  //   dark: "#7c3aed",
  //   light: "rgba(139, 92, 246, 0.1)",
  //   lighter: "rgba(139, 92, 246, 0.05)",
  //   shadow: "rgba(139, 92, 246, 0.35)",
  //   shadowHover: "rgba(139, 92, 246, 0.45)",
  //   shadowLight: "rgba(139, 92, 246, 0.3)",
  //   shadowLighter: "rgba(139, 92, 246, 0.4)",
  // },

  // Secondary colors (Navy Blue - from airplane & text)
  secondary: {
    main: "#1B365D", // Main navy (logo text)
    dark: "#142847", // Darker navy
    light: "#264573", // Lighter navy
    lighter: "#3A5A80", // Even lighter
    bg: "rgba(27, 54, 93, 0.1)",
    bgLight: "rgba(27, 54, 93, 0.05)",
    shadow: "rgba(27, 54, 93, 0.35)",
    shadowHover: "rgba(27, 54, 93, 0.45)",
  },

  // Tertiary colors (Teal/Cyan - from swoosh/trail)
  tertiary: {
    main: "#00A6A6", // Main teal
    dark: "#008B8B", // Darker teal
    light: "#33BCBC", // Lighter teal
    lighter: "#66D2D2", // Very light teal
    cyan: "#5BC5D9", // Cyan from trail
    cyanLight: "#7DD4E4", // Light cyan
    cyanDark: "#3EB0C4", // Dark cyan
    gradient: "linear-gradient(135deg, #00A6A6 0%, #5BC5D9 100%)",
    bg: "rgba(0, 166, 166, 0.1)",
    bgLight: "rgba(0, 166, 166, 0.05)",
    shadow: "rgba(0, 166, 166, 0.35)",
  },

  // Accent colors
  accent: {
    orange: "#F7941D", // Brand orange
    orangeLight: "#FFA94D",
    orangeDark: "#E8850A",
    teal: "#00A6A6", // Brand teal
    tealLight: "#5BC5D9",
    navy: "#1B365D", // Brand navy
    red: "#e63946",
    redDark: "#d00f1e",
    redLight: "#ff6663",
  },

  // ========================================
  // SEMANTIC COLORS
  // ========================================

  // Success colors (Green)
  success: {
    main: "#4caf50",
    dark: "#10b981",
    light: "#8cd64e",
    bg: "rgba(76, 175, 80, 0.1)",
  },

  // Error/Danger colors (Red)
  error: {
    main: "#e53935",
    dark: "#dc2626",
    bright: "#ef4444",
    light: "#fef2f2",
    bg: "rgba(229, 57, 53, 0.03)",
    shadow: "rgba(229, 57, 53, 0.1)",
  },

  // Warning colors (Using brand orange)
  warning: {
    main: "#F7941D",
    dark: "#E8850A",
    light: "#FFA94D",
    gold: "#ffd700",
    beige: "#ffe4b5",
    bg: "rgba(247, 148, 29, 0.1)",
  },

  // Info colors (Using brand teal)
  info: {
    main: "#00A6A6",
    light: "#5BC5D9",
    dark: "#008B8B",
    steel: "#4682b4",
    bg: "rgba(0, 166, 166, 0.1)",
  },

  // ========================================
  // BOOKING STATUS COLORS
  // ========================================

  status: {
    initial: "#8B0000",
    paySuccess: "#FFD700",
    bookingDone: "#c49000",
    inProgressBook: "#c49000",
    ticketingDone: "#4caf50",
    certificateIssued: "#4caf50",
    ticketingFail: "#c49000",
    exchange: "#FFE4B5",
    void: "#4B0082",
    orderFail: "#c49000",
    bookingFail: "#c49000",
    payFail: "#c49000",
    bookingCancelFromClient: "#ff0302",
    bookingCancelFromAdmin: "#ff0302",
    bookingCancelError: "#c49000",
    applicationRefund: "#87CEEB",
    partialRefund: "#4682B4",
    bookingCancel: "#ff0302",
    refund: "#4682B4",
    balancePaymentRequest: "#4390ae",
    balancePaymentApproved: "#45b0b0",
  },

  // ========================================
  // UI ELEMENT COLORS
  // ========================================

  // Background colors
  background: {
    page: "#f0f4f7",
    light: "#f5f5f5",
    lighter: "#fafafa",
    white: "#ffffff",
    overlay: "rgba(27, 54, 93, 0.5)", // Navy overlay
    overlayLight: "rgba(27, 54, 93, 0.3)",
    brandGradient: "linear-gradient(135deg, #F7941D 0%, #E8850A 100%)",
    tealGradient: "linear-gradient(135deg, #00A6A6 0%, #5BC5D9 100%)",
  },

  // Border colors
  border: {
    main: "#e5e5e5",
    light: "#eeeeee",
    medium: "#d0d0d0",
    dark: "#b0b0b0",
    darker: "#ccc",
    focus: "#00A6A6", // Teal focus
    focusAlt: "#F7941D", // Orange focus alternative
    error: "#e53935",
    subtle: "rgba(0, 0, 0, 0.04)",
    subtleMedium: "rgba(0, 0, 0, 0.06)",
  },

  // Shadow colors
  shadow: {
    light: "rgba(0, 0, 0, 0.06)",
    medium: "rgba(0, 0, 0, 0.1)",
    mediumPlus: "rgba(0, 0, 0, 0.12)",
    dark: "rgba(0, 0, 0, 0.15)",
    darker: "rgba(0, 0, 0, 0.2)",
    heavy: "rgba(0, 0, 0, 0.5)",
    extraHeavy: "rgba(0, 0, 0, 0.7)",
    brand: "rgba(247, 148, 29, 0.25)", // Orange shadow
    brandHover: "rgba(247, 148, 29, 0.35)",
    teal: "rgba(0, 166, 166, 0.25)", // Teal shadow
  },

  // Text colors
  text: {
    primary: "#1B365D", // Navy - main text
    primaryDark: "#142847",
    primaryDarker: "#0D1B2A",
    secondary: "#3A5A80", // Lighter navy
    secondaryLight: "#5C7A9E",
    muted: "#64748b",
    mutedLight: "#94a3b8",
    mutedLighter: "#b0bec5",
    disabled: "#b7b7b7",
    white: "#ffffff",
    link: "#00A6A6", // Teal links
    linkHover: "#008B8B",
    accent: "#F7941D", // Orange accent text
  },

  // Button colors
  button: {
    primary: "#F7941D", // Orange primary button
    primaryHover: "#E8850A",
    primaryActive: "#D47600",
    secondary: "#00A6A6", // Teal secondary
    secondaryHover: "#008B8B",
    tertiary: "#1B365D", // Navy tertiary
    tertiaryHover: "#264573",
    outline: "#ffffff",
    outlineHover: "#f5f5f5",
    disabled: "#e5e5e5",
    disabledText: "#999999",
    disabledBorder: "#b0b0b0",
  },

  // ========================================
  // HEADER COLORS
  // ========================================

  header: {
    background: "#ffffff",
    backgroundAlt: "#1B365D", // Navy header variant
    text: "#1B365D",
    textMuted: "#64748b",
    textSecondary: "#94a3b8",
    textWhite: "#ffffff",
    hoverBg: "#f1f5f9",

    // Primary accent (Orange)
    primary: "#F7941D",
    primaryDark: "#E8850A",
    primaryLight: "rgba(247, 148, 29, 0.1)",
    primaryShadow: "rgba(247, 148, 29, 0.35)",
    primaryShadowHover: "rgba(247, 148, 29, 0.45)",

    // Secondary accent (Teal)
    secondary: "#00A6A6",
    secondaryLight: "#5BC5D9",

    // Status colors
    success: "#10b981",
    danger: "#ef4444",
    dangerDark: "#dc2626",
    dangerBg: "#fef2f2",

    // Border & Shadow
    border: "rgba(0, 0, 0, 0.04)",
    shadow: "rgba(0, 0, 0, 0.06)",

    // Avatar
    avatarGradientStart: "#F7941D",
    avatarGradientEnd: "#E8850A",
    avatarShadow: "rgba(247, 148, 29, 0.3)",
    avatarShadowHover: "rgba(247, 148, 29, 0.4)",

    // Dropdown
    dropdownBg: "#ffffff",
    dropdownBorder: "rgba(0, 0, 0, 0.06)",
    dropdownShadow: "rgba(0, 0, 0, 0.12)",
    dropdownDivider: "#f1f5f9",
    dropdownItemText: "#1B365D",

    // Mobile menu
    mobileOverlay: "rgba(27, 54, 93, 0.5)",
    mobilePanelShadow: "rgba(0, 0, 0, 0.15)",
  },

  // ========================================
  // INPUT/FORM COLORS
  // ========================================

  input: {
    border: "#ccc",
    borderFocus: "#00A6A6", // Teal focus
    borderFocusAlt: "#F7941D", // Orange focus alternative
    borderError: "#e53935",
    borderSuccess: "#4caf50",
    errorText: "#e53935",
    errorBg: "rgba(229, 57, 53, 0.03)",
    focusShadow: "rgba(0, 166, 166, 0.15)",
    focusShadowAlt: "rgba(247, 148, 29, 0.15)",
    errorShadow: "rgba(229, 57, 53, 0.1)",
    labelText: "#5C7A9E",
  },

  // ========================================
  // LEGACY COLORS (for backwards compatibility)
  // ========================================

  headerBg: "#1B365D", // Navy
  footerBg: "#1B365D", // Navy footer
  searchFormBg: "rgba(27, 54, 93, 0.85)", // Navy with transparency
  searchButtonBg: "#F7941D", // Orange
  removeRouteButtonBg: "#ff6663",
  hoverButtonBg: "#E8850A", // Dark orange

  // ========================================
  // FLIGHT SEARCH SPECIFIC
  // ========================================

  flight: {
    cardBg: "#ffffff",
    cardBorder: "#e5e5e5",
    cardHoverBorder: "#00A6A6",
    cardHoverShadow: "rgba(0, 166, 166, 0.15)",
    priceText: "#F7941D",
    priceHighlight: "#E8850A",
    airlineLogo: "#1B365D",
    routeLine: "#5BC5D9",
    layoverBg: "#f0f4f7",
    selectedBg: "rgba(0, 166, 166, 0.05)",
    selectedBorder: "#00A6A6",
  },

  // ========================================
  // BRAND SPECIFIC
  // ========================================

  brand: {
    // Main brand colors from logo
    orange: "#F7941D",
    navy: "#1B365D",
    teal: "#00A6A6",
    cyan: "#5BC5D9",

    // Logo colors for reference
    logoOrange: "#F7941D",
    logoNavy: "#1B365D",
    logoTealDark: "#00A6A6",
    logoTealLight: "#5BC5D9",

    // Gradient (for swoosh effect)
    swooshGradient: "linear-gradient(90deg, #00A6A6 0%, #5BC5D9 50%, #1B365D 100%)",
  },
};

export default palette;
